import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Typography, useMediaQuery, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import ListView from '../../SystemDesign/ListViewV2';
import DateInTable from '../../SystemDesign/DateInTable';
import EvidenceFileIcon from '../../SystemDesign/EvidenceFileIcon';
import DeletePractileEvidence from './PracticalEvidences/DeletePractileEvidence';
import ApprovePracticalEvidence from './PracticalEvidences/ApprovePracticalEvidence';
import RejectPracticalEvidence from './PracticalEvidences/RejectPracticalEvidence';
import { formattedDate } from '../../Utils/Functions';
import { useListPracticalEvidencesQuery } from '../../services/SkillsManagement';
import { SubTitleScreen, TitleScreen } from '../../SystemDesign/Styles';
import ListViewMenuActions from '../../SystemDesign/ListViewMenuActions';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../Reducers/Actions';
import { AuthContext } from '../../Context/AuthContext';
import DownloadPracticalEvidence from "./PracticalEvidences/DownloadPracticalEvidence";

function resolveBackendBase() {
  let envBase =
    (typeof import.meta !== 'undefined' &&
      import.meta.env &&
      import.meta.env.VITE_BACKEND_URL) ||
    process.env.REACT_APP_BACKEND_URL ||
    '';
  envBase = (envBase || '').trim();
  if (!envBase) return window.location.origin;
  try {
    const url = new URL(envBase, window.location.origin);
    const origin = url.origin;
    const path = (url.pathname || '').replace(/\/$/, '');
    return `${origin}${path}`;
  } catch {
    return window.location.origin;
  }
}

async function downloadEvidenceById(evidenceId, fileName) {
  if (!evidenceId) return;
  const base = resolveBackendBase();
  const href = `${base}/skills-management/practical-evidences/${evidenceId}/download`;
  let token;
  try {
    const stored = localStorage.getItem('user');
    if (stored) token = JSON.parse(stored)?.token;
  } catch (_) {}
  try {
    const res = await fetch(href, {
      method: 'GET',
      headers: {
        Accept: 'application/octet-stream,application/pdf,image/*,video/*',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      cache: 'no-cache',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const dispo = res.headers.get('content-disposition') || '';
    let suggested = fileName || `evidence-${evidenceId}`;
    const match = dispo.match(/filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i);
    if (match) suggested = decodeURIComponent(match[1] || match[2] || suggested);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = suggested;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    window.open(href, '_blank', 'noopener');
  }
}

const TypeChip = ({ type }) => {
  if (!type) return null;
  return <Chip size="small" label={String(type)} color="primary" variant="outlined" />;
};

/**
 * Props:
 *  - queryParams: { pagination(JSON), sorting(JSON), filters(JSON), search? }
 *  - setQueryParams: function(next)
 *
 * Backend pagination params supported (from Swagger):
 *  currentPage, rowsPerPage, column, direction, evaluation_id, type, employee_id
 */
const EvidencesFileList = ({ queryParams, setQueryParams }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMdOrLarger = useMediaQuery((t) => t.breakpoints.up('md'));

  // Local fallback for query params if not provided by parent
  const [localQueryParams, setLocalQueryParams] = useState(() => ({
    pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 10 }),
    sorting: JSON.stringify({ column: 'createdAt', direction: 'desc' }),
    filters: '',
  }));
  const qp = queryParams ?? localQueryParams;
  const setQP = setQueryParams ?? setLocalQueryParams;
  const paginationState = (() => {
    try {
      return JSON.parse(qp.pagination || '{}');
    } catch {
      return { currentPage: 1, rowsPerPage: 10 };
    }
  })();
  const sortingState = (() => {
    try {
      return JSON.parse(qp.sorting || '{}');
    } catch {
      return {};
    }
  })();
  const filtersState = (() => {
    try {
      return JSON.parse(qp.filters || '{}');
    } catch {
      return {};
    }
  })();
  // Employee filter state (server-side)
  const { user } = useContext(AuthContext);

  const currentPage = Number(paginationState.currentPage || 1);
  const rowsPerPage = Number(paginationState.rowsPerPage || 10);
  const column = sortingState.column || 'createdAt';
  const direction = sortingState.direction || 'desc';
  // Accept both snake_case and camelCase from filters and forward camelCase to API
  const evaluationId = filtersState.evaluationId || filtersState.evaluation_id || undefined;
  const selectedType = Array.isArray(filtersState.type)
    ? (filtersState.type[0] ?? '')
    : (filtersState.type ?? '');
  const type = selectedType || undefined;
  // Employee filter removed

  // Server fetch (RTK Query)
  const { data, isFetching } = useListPracticalEvidencesQuery({
    currentPage,
    rowsPerPage,
    column,
    direction,
    evaluationId,
    type,
  });
  const dispatch = useDispatch();
  const storeEvidencesState = useSelector((s) => s?.practicalEvidences);

  // Initialize/merge fetched page into reducer for real-time updates
  useEffect(() => {
    const fetched = Array.isArray(data?.data?.evidences) ? data.data.evidences : [];
    if (!isFetching) {
      dispatch({ type: actions.INITIALIZE_PRACTICAL_EVIDENCES, payload: fetched });
    }
  }, [dispatch, isFetching, data?.data?.evidences]);

  // Use the fetched server page to honor pagination, then overlay local store updates (approve/reject) for real-time UI.
  const fetchedEvidences = Array.isArray(data?.data?.evidences) ? data.data.evidences : [];
  const storeItems = useMemo(() => {
    const arr = Array.isArray(storeEvidencesState) ? storeEvidencesState : (storeEvidencesState?.items || []);
    return Array.isArray(arr) ? arr : [];
  }, [storeEvidencesState]);
  const deletedIds = useMemo(() => {
    const del = Array.isArray(storeEvidencesState?.tombstones) ? storeEvidencesState.tombstones : [];
    return new Set(del.map((x) => String(x)));
  }, [storeEvidencesState]);
  const storeById = useMemo(() => {
    const m = new Map();
    for (const ev of storeItems) {
      const id = ev?.evidence_id ?? ev?.id;
      if (id != null) m.set(String(id), ev);
    }
    return m;
  }, [storeItems]);
  const baseList = useMemo(() => {
    // Overlay store item over fetched item when IDs match, and drop items marked deleted
    const merged = [];
    for (const ev of fetchedEvidences) {
      const id = ev?.evidence_id ?? ev?.id;
      const key = id != null ? String(id) : undefined;
      if (key && deletedIds.has(key)) continue; // filter deleted
      merged.push((key && storeById.has(key)) ? { ...ev, ...storeById.get(key) } : ev);
    }
    return merged;
  }, [fetchedEvidences, storeById, deletedIds]);

  // Apply client-side filtering for type to avoid showing non-matching items
  const evidences = useMemo(() => {
    if (!type) return baseList;
    const wanted = String(type).toLowerCase();
    return baseList.filter((ev) => String(ev?.evidence_type ?? ev?.evidenceType ?? '').toLowerCase() === wanted);
  }, [baseList, type]);
  // When a type filter is applied, reflect the filtered length in total to avoid showing extra rows/pages
  const total = type
    ? evidences.length
    : Number(data?.data?.pagination?.total || evidences.length);
  const serverCurrentPage = Number(data?.data?.pagination?.currentPage || currentPage);
  const serverRowsPerPage = Number(data?.data?.pagination?.rowsPerPage || rowsPerPage);

  // Employee filter removed

  const columns = useMemo(
    () => [
      {
        id: 'file',
        header: 'File',
        accessorFn: (row) => row,
        cell: ({ row }) => {
          const ev = row?.original || {};
          const name =
            ev?.file_name ||
            (ev?.evidence_url ? String(ev.evidence_url).split('/').pop() : 'Evidence');
          const id = ev?.evidence_id || ev?.id;
          if (!id) return name;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EvidenceFileIcon
                evidence={ev}
                title={name}
                onClick={() => downloadEvidenceById(id, name)}
              />
              <Typography variant="body2" noWrap title={name}>
                {name}
              </Typography>
            </Box>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'evidence_type',
        cell: (info) => <TypeChip type={info.getValue()} />,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'employeeUsername',
        header: 'Employee',
        accessorFn: (row) =>
          row?.evaluation?.employee?.username ||
          row?.employee?.username ||
          row?.employeeUsername ||
          row?.username ||
          '',
        cell: (info) => {
          const username = info.getValue();
          return (
            <Typography variant="body2" noWrap title={username}>
              {username || '-'}
            </Typography>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: (info) => {
          const s = String(info.getValue() || 'pending').toLowerCase();
          const label = s === 'approved' ? 'Approved' : s === 'rejected' ? 'Rejected' : 'Pending';
          const colorOverride =
            s === 'approved'
              ? colors.green[700]
              : s === 'rejected'
              ? colors.redAccent[600]
              : colors.Orange[400];
          const bgColorOverride =
            s === 'approved'
              ? colors.green[100]
              : s === 'rejected'
              ? colors.redAccent[100]
              : colors.Orange[100];
          return <Chip size="small" label={label} sx={{ color: colorOverride, bgcolor: bgColorOverride }} />;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      ...(isMdOrLarger
        ? [
            {
              id: 'created',
              header: 'Created',
              accessorKey: 'createdAt',
              cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))} />,
              enableColumnFilter: false,
              enableSorting: false,
            },
          ]
        : []),
      ...(isMdOrLarger
        ? [
            {
              id: 'updated',
              header: 'Updated',
              accessorKey: 'updatedAt',
              cell: (info) => <DateInTable date={formattedDate(new Date(info.getValue()))} />,
              enableColumnFilter: false,
              enableSorting: false,
            },
          ]
        : []),
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const ev = row?.original || {};
          const evidenceId = ev?.evidence_id || ev?.id;
          const name = ev.original_name
              || ev.client_name
              || ev.file_name
              || (ev.evidence_url ? ev.evidence_url.split('/').pop() : 'Evidence');
          return (
            <ListViewMenuActions>
              {evidenceId ? <ApprovePracticalEvidence evidenceId={evidenceId} /> : null}
              {evidenceId ? <RejectPracticalEvidence evidenceId={evidenceId} /> : null}
              <DeletePractileEvidence row={{ original: ev }} />
              <DownloadPracticalEvidence evidenceId={evidenceId} fileName={name} />
            </ListViewMenuActions>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    [colors, isMdOrLarger]
  );

  // Show all supported top-level types across all pages (server accepts these for filtering)
  const ALL_EVIDENCE_TYPES = useMemo(() => ['image', 'video', 'document'], []);
  const typeOptions = ALL_EVIDENCE_TYPES;

  const pagination = {
    currentPage: serverCurrentPage,
    rowsPerPage: serverRowsPerPage,
    total,
    totalPages: Math.ceil(total / serverRowsPerPage) || 1,
  };

  // Push changes back up to parent -> triggers refetch
  const applyFilters = (filters, paginationInfo, sortingInfo, search) => {
    const next = { ...(qp || {}) };
    if (filters) next.filters = JSON.stringify(filters);
    if (paginationInfo) next.pagination = JSON.stringify(paginationInfo);
    if (sortingInfo) next.sorting = JSON.stringify(sortingInfo);
    if (typeof search !== 'undefined') next.search = search;
    setQP(next);
  };

  const handleTypeChange = (event) => {
    const val = event.target.value || '';
    const nextFilters = { ...filtersState };
    if (val) nextFilters.type = [String(val).toLowerCase()];
    else delete nextFilters.type;
    applyFilters(nextFilters, { currentPage: 1, rowsPerPage }, sortingState);
  };

  return (
    <Box p={3}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
        <div>
          <Typography sx={TitleScreen(colors)}>Evidences</Typography>
          <Typography sx={SubTitleScreen(colors)}>
            Browse and manage evidence files.
          </Typography>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ minWidth: 220 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="evidence-type-label">Type</InputLabel>
              <Select
                labelId="evidence-type-label"
                label="Type"
                value={selectedType || ''}
                onChange={handleTypeChange}
              >
                <MenuItem value="">
                  <em>All types</em>
                </MenuItem>
                {typeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          {/* Employee filter removed */}
        </div>
      </div>
      <ListView
        dispatchData={evidences}
        columns={columns}
        tableName={'evidences-list'}
        columnFilters={[]}
        setColumnFilters={() => {}}
        applyFilters={applyFilters}
        pagination={pagination}
        setQueryParams={setQP}
        queryParams={qp}
        showColumnManager={false}
        manualPagination
        loading={isFetching}
      />
    </Box>
  );
};

export default EvidencesFileList;